import { FieldArray, useFormikContext } from 'formik'
import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import Fieldset from '../../Fieldset'
import { Stack } from '@fluentui/react'
import { DateTime } from 'luxon'
import LineItemGroups from './LineItemGroups'
import { AccountingId, InvoiceMessage, InvoiceMutationDto, InvoiceStatus, InvoiceType } from '../../../../config/finance/invoiceConfig'
import FinanceUnitField from './FinanceUnitField'
import SingleUnitDimensionFields from '../../Accounting/SingleUnitDimensionFields'
import { getFilePartyFromMeta, InvoiceProps } from './InvoiceForm'
import useUnits from '../../../../hooks/useUnits'
import { FormikHelpers } from 'formik/dist/types'
import { DimensionsDto } from '../../Accounting/DimensionFields'
import { Field, Link, makeStyles, MessageBar, MessageBarBody, shorthands, tokens } from '@fluentui/react-components'
import useModules from '../../../../hooks/module/useModules'
import BaseCombobox from '../../../Combobox/BaseCombobox'
import { ModuleType } from '../../../../context/GlobalContext'
import InvoiceChildren from './InvoiceChildren'
import { useHistory } from 'react-router-dom'
import BankTransactions from './BankTransactions'
import { useTranslation } from 'react-i18next'
import FormikField from '../../../Formik/FormikField'
import LanguageCombobox, { LanguageComboboxProps } from '../../../Combobox/i18n/LanguageCombobox'
import CurrencyCombobox, { CurrencyComboboxProps } from '../../../Combobox/i18n/CurrencyCombobox'
import FormContainer from '../../FormContainer'
import AttributesList from '../../Attributes/AttributesList'
import useAttributes, { AttributeContextType, AttributeViewType, EntityAttributeType } from '../../../../hooks/useAttributes'
import MoneyInput, { MoneyInputProps } from '../../../Input/MoneyInput'
import { CommitEventType } from '../../../Input/FormattableNumberInput'
import { EntityType } from '../../Accounting/EntityField'
import FormikTextareaField from '../../../Formik/FormikTextareaField'
import FormikInputField from '../../../Formik/FormikInputField'
import LocalDatePicker, { LocalDatePickerProps } from '../../../DatePicker/LocalDatePicker'
import { useCountdown } from 'usehooks-ts'
import useAxios from '../../../../hooks/useAxios'
import { usePrevious } from '@fluentui/react-hooks'
import { formatMultipleEmailAddresses } from '../../../../helpers/regexHelper'
import OrganisationProfileCombobox from '../../../Combobox/OrganisationProfileCombobox'
import useOrganisationProfiles from '../../../../hooks/useOrganisationProfiles'

const useStyles = makeStyles({
    root: { flexGrow: 1, flexBasis: 0, minWidth: 0 },
    alert: {
        ...shorthands.margin(tokens.spacingHorizontalS),
    },
})

const filterMessages = (emailMessages: InvoiceMessage[], language: string, currency?: string): InvoiceMessage => {
    const message: InvoiceMessage = (emailMessages || [])
        .filter(message => message.language === language)
        .sort((a, b) => (a.currency || 'zzz').localeCompare(b.currency || 'zzz'))
        .find(message => message.currency === currency)

    if (!message && currency) {
        return filterMessages(emailMessages, language, null)
    }

    return message
}

const setMessage = (message: InvoiceMessage, setFieldValue: FormikHelpers<InvoiceMutationDto>['setFieldValue']): void => {
    setFieldValue('messageSubject', message?.subject)
    setFieldValue('messageBody', message?.body?.replaceAll('<br/>', '\n'))
}

const setPaymentMessage = (message: InvoiceMessage, setFieldValue: FormikHelpers<InvoiceMutationDto>['setFieldValue']): void => {
    setFieldValue('paymentMessageBody', message?.body?.replaceAll('<br/>', '\n'))
}

export default function InvoiceFieldSets(props: InvoiceProps): ReactElement {
    const styles = useStyles()

    const axios = useAxios()
    const history = useHistory()
    const modules = useModules()
    const { loaded: organisationProfilesLoaded, organisationProfiles } = useOrganisationProfiles()
    const { t: translate } = useTranslation()
    const { units } = useUnits()

    const { activeAttributeDefinitions } = useAttributes(AttributeViewType.Input, [AttributeContextType.Invoice])

    const { hasParties, invoice, meta, useInitialInvoice } = props
    const { initialValues, values, setFieldValue } = useFormikContext<InvoiceMutationDto>()
    const previousValues = usePrevious(values)

    const isEndUserInvoice = useMemo<boolean>(() => ![InvoiceType.Split].includes(values.type), [values.type])
    const isSplitInvoice = useMemo<boolean>(() => [InvoiceType.Split].includes(values.type), [values.type])
    const isSubSplitInvoice = useMemo<boolean>(() => [InvoiceType.SubSplit].includes(values.type), [values.type])

    const firstFile = useMemo(() => meta.data.files?.[0]?.file, [meta.data.files])
    const fileParties = useMemo(() => meta.data.files?.flatMap(file => file.fileParties) || [], [meta.data.files])

    const [transactionSavedCount, transactionSavedCounter] = useCountdown({
        countStart: 0,
        countStop: 4,
        intervalMs: 1000,
        isIncrement: true,
    })

    useEffect(() => {
        if (values.status === InvoiceStatus.New) {
            setFieldValue('number', meta.data.number)
        }

        setFieldValue(
            'name',
            (useInitialInvoice && invoice.name) ||
                (values.fileIds?.length === 1 && (firstFile?.invoiceName || firstFile?.name)) ||
                meta.data.files.map(file => file.file.reference).join(', '),
        )
        setFieldValue('currency', (useInitialInvoice && invoice.price?.currency) || firstFile?.currency || 'EUR')
        setFieldValue('language', (useInitialInvoice && invoice.language) || firstFile?.language)
        setFieldValue('period', useInitialInvoice && invoice.period)

        setFieldValue(
            'unit',
            useInitialInvoice
                ? invoice.unit
                : (firstFile?.unit && units.find(unit => firstFile?.unit === unit.key)?.key) || (units.length === 1 && units[0].key),
        )
    }, [meta.data, units, isEndUserInvoice])

    useEffect(() => {
        setFieldValue(
            'accounting',
            useInitialInvoice && invoice.unit === values.unit ? initialValues.accounting : firstFile?.accounting?.[values.unit],
        )
    }, [meta.data, values.unit, isEndUserInvoice])

    // Updates when language or currency changes.
    useEffect(() => {
        if (isEndUserInvoice && values.language) {
            if (useInitialInvoice) {
                setMessage(invoice.message, setFieldValue)
            } else {
                setMessage(filterMessages(meta.data.emailMessages, values.language, firstFile?.paymentCurrency || values.currency), setFieldValue)
            }
        }
    }, [meta.data.emailMessages, values.language, values.currency])

    // Updates when language changes.
    useEffect(() => {
        if (isEndUserInvoice && values.language) {
            if (useInitialInvoice) {
                setPaymentMessage(invoice.paymentMessage, setFieldValue)
            } else {
                setPaymentMessage(
                    filterMessages(meta.data.paymentMessages, values.language, firstFile?.paymentCurrency || values.currency),
                    setFieldValue,
                )
            }
        }
    }, [meta.data.paymentMessages, values.language, values.currency])

    useEffect(() => {
        const template = values.templateId && props.templates?.find(template => template.id === values.templateId)
        let term = template?.term ?? 14

        if (useInitialInvoice && invoice.recipient?.debtorId === values.debtorPivotId) {
            term = invoice.term ?? term
        } else if (!template || template.type === InvoiceType.Regular) {
            term = getFilePartyFromMeta(values.debtorPivotId, null, undefined, fileParties)?.[0]?.term || term
        }
        setFieldValue('term', term)
    }, [values.debtorPivotId, meta?.data?.files])

    // Updates accounting transaction when this is edited manually.
    useEffect(() => {
        if (
            invoice?.id &&
            !props.isEditable &&
            values.accounting &&
            values.accounting.transaction &&
            previousValues?.accounting &&
            values.accounting.transaction.code !== previousValues?.accounting?.transaction?.code
        ) {
            axios
                .put<any, any, AccountingId>(`/finance/invoice/${invoice.id}/transaction`, values.accounting.transaction)
                .then(() => setFieldValue('transaction', values.accounting.transaction))
                .then(() => (invoice.transaction = values.accounting.transaction))
                .then(() => transactionSavedCounter.startCountdown())
        }
    }, [values.accounting])

    useEffect(() => {
        if (transactionSavedCount >= 4) {
            transactionSavedCounter.resetCountdown()
        }
    }, [transactionSavedCount])

    if (!hasParties) {
        return <></>
    }

    return (
        <Stack tokens={{ childrenGap: 'm' }}>
            <Fieldset legend={translate('invoice.edit.form.payment.label')} canToggle>
                <Stack horizontal tokens={{ childrenGap: 'm' }}>
                    <FormikInputField<InvoiceMutationDto>
                        required
                        fieldName="number"
                        labelKey="invoice.edit.form.payment.number"
                        className={styles.root}
                        componentProps={{
                            readOnly: !props.isEditable,
                        }}
                    />
                    <FormikField<LocalDatePickerProps, InvoiceMutationDto>
                        required
                        fieldName="date"
                        labelKey="invoice.edit.form.payment.date"
                        component={LocalDatePicker}
                        componentProps={{
                            readOnly: !props.isEditable,
                            isoValue: values.date,
                            onSelectDate: date => setFieldValue('date', date),
                            minDate: DateTime.local().minus({ years: 1 }).toBSON(),
                            maxDate: DateTime.local().plus({ years: 1 }).toBSON(),
                        }}
                        className={styles.root}
                    />
                    <FormikInputField<InvoiceMutationDto>
                        required
                        fieldName="term"
                        labelKey="invoice.edit.form.payment.term"
                        componentProps={{
                            readOnly: !props.isEditable,
                            contentBefore: translate('invoice.edit.form.header.term_prefix') || undefined,
                            contentAfter: translate('invoice.edit.form.header.term_suffix') || undefined,
                        }}
                        className={styles.root}
                    />
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 'm' }}>
                    <FormikField<CurrencyComboboxProps, InvoiceMutationDto>
                        required
                        labelKey="invoice.edit.form.payment.currency"
                        fieldName={'currency'}
                        component={CurrencyCombobox}
                        componentProps={{
                            readOnly: !props.isEditable,
                            selectedOptions: [values.currency],
                            onOptionSelect: (_, data) => {
                                setFieldValue('currency', data.selectedOptions?.[0])
                            },
                        }}
                    />
                    {(!!firstFile?.paymentCurrency || !!values.paymentPrice) && (
                        <FormikField<MoneyInputProps, InvoiceMutationDto>
                            labelKey="invoice.edit.form.payment.paymentprice"
                            fieldName={'paymentPrice'}
                            component={MoneyInput}
                            componentProps={{
                                currency: firstFile?.paymentCurrency,
                                onCommit: (eventType: CommitEventType, value: string) => {
                                    if (eventType !== CommitEventType.OnLoad) {
                                        setFieldValue('paymentPrice', value)
                                    }
                                },
                            }}
                        />
                    )}
                </Stack>
            </Fieldset>
            <Fieldset legend={translate('invoice.edit.form.invoiceline.label')} canToggle open>
                {!isSubSplitInvoice && (
                    <FieldArray name="lineItemGroups">{arrayHelpers => <LineItemGroups {...arrayHelpers} {...props} />}</FieldArray>
                )}
                {isSubSplitInvoice && (
                    <MessageBar intent={'info'}>
                        <MessageBarBody>
                            {translate('invoice.edit.info.split')}{' '}
                            <Link onClick={() => history.push(`/invoice/${invoice.parent?.id}/edit`)}>{invoice.parent?.number}</Link>.
                        </MessageBarBody>
                    </MessageBar>
                )}
            </Fieldset>
            {isSplitInvoice && (
                <Fieldset legend={translate('invoice.edit.form.debtors.label')} canToggle open>
                    <FieldArray name="children">{arrayHelpers => <InvoiceChildren {...arrayHelpers} {...props} />}</FieldArray>
                </Fieldset>
            )}
            {isEndUserInvoice && (
                <Fieldset legend={translate('invoice.edit.form.correspondence.label')} canToggle>
                    <Stack horizontal tokens={{ childrenGap: 'm' }}>
                        <FormikInputField<InvoiceMutationDto>
                            fieldName="recipientEmail"
                            labelKey="invoice.edit.form.header.recipientemail"
                            className={styles.root}
                            componentProps={{
                                readOnly: !props.isEditable,
                                onBlur: e => {
                                    const newValue = formatMultipleEmailAddresses(e.target.value)
                                    setFieldValue('recipientEmail', newValue?.join(';'))
                                },
                            }}
                        />
                        <FormikInputField<InvoiceMutationDto>
                            required={values.recipientReferenceRequired}
                            fieldName="recipientReference"
                            labelKey="invoice.edit.form.header.recipientreference.label"
                            className={styles.root}
                            componentProps={{
                                readOnly: !props.isEditable,
                            }}
                        />
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 'm' }}>
                        <FormikInputField<InvoiceMutationDto>
                            fieldName="name"
                            labelKey="invoice.edit.form.correspondence.name"
                            className={styles.root}
                            componentProps={{
                                readOnly: !props.isEditable,
                            }}
                        />
                        <FormikField<LanguageComboboxProps, InvoiceMutationDto>
                            required
                            labelKey="invoice.edit.form.correspondence.language"
                            fieldName={'language'}
                            component={LanguageCombobox}
                            componentProps={{
                                readOnly: !props.isEditable,
                                selectedOptions: [values.language],
                                onOptionSelect: (_, data) => {
                                    setFieldValue('language', data.selectedOptions?.[0])
                                },
                            }}
                        />
                    </Stack>

                    <FormikInputField<InvoiceMutationDto>
                        fieldName="messageSubject"
                        labelKey="invoice.edit.form.correspondence.messagesubject"
                        className={styles.root}
                        componentProps={{
                            readOnly: !props.isEditable,
                        }}
                    />
                    <FormikTextareaField<InvoiceMutationDto>
                        fieldName="messageBody"
                        labelKey="invoice.edit.form.correspondence.messagebody"
                        className={styles.root}
                        componentProps={{
                            readOnly: !props.isEditable,
                            rows: 3,
                            resize: 'vertical',
                        }}
                    />
                    <FormikTextareaField<InvoiceMutationDto>
                        fieldName="paymentMessageBody"
                        labelKey="invoice.edit.form.correspondence.paymentmessagebody"
                        className={styles.root}
                        componentProps={{
                            readOnly: !props.isEditable,
                            rows: 3,
                            resize: 'vertical',
                        }}
                    />
                </Fieldset>
            )}

            {modules.has(ModuleType.Accounting) && values.unit && (
                <Fieldset legend={translate('invoice.edit.form.accounting.label')} canToggle>
                    <SingleUnitDimensionFields
                        values={values.accounting}
                        setValue={(property: keyof DimensionsDto, item: AccountingId) => setFieldValue(`accounting.${property}`, item)}
                        unitKey={values.unit}
                        selectedEntityTypes={[EntityType.CostCenter, EntityType.CostUnit, EntityType.GLA, EntityType.Project, EntityType.Transaction]}
                        readOnlyEntityTypes={
                            !props.isEditable ? [EntityType.CostCenter, EntityType.CostUnit, EntityType.GLA, EntityType.Project] : []
                        }
                    />
                    {transactionSavedCount > 0 && transactionSavedCount < 4 && (
                        <MessageBar intent={'success'}>
                            <MessageBarBody>{translate('invoice.action.accounting.transaction.saved')}</MessageBarBody>
                        </MessageBar>
                    )}
                </Fieldset>
            )}

            <Fieldset legend={translate('invoice.edit.form.other.label')} canToggle>
                {organisationProfilesLoaded && organisationProfiles?.length > 0 && (
                    <Field label={translate('invoice.edit.form.other.organisationprofileid')}>
                        <OrganisationProfileCombobox
                            selectedOptions={[values.organisationProfileKey]}
                            onOptionSelect={(_, data) => setFieldValue('organisationProfileKey', data.optionValue)}
                        />
                    </Field>
                )}
                {isEndUserInvoice && (
                    <FormikTextareaField<InvoiceMutationDto>
                        labelKey={'invoice.edit.form.other.description'}
                        fieldName="description"
                        componentProps={{
                            readOnly: !props.isEditable,
                            rows: 3,
                        }}
                    />
                )}
                <FormikTextareaField<InvoiceMutationDto>
                    labelKey={'invoice.edit.form.other.note'}
                    fieldName="note"
                    componentProps={{
                        readOnly: !props.isEditable,
                        rows: 3,
                    }}
                />
                <Stack horizontal tokens={{ childrenGap: 's1' }}>
                    <FinanceUnitField readOnly={!props.isEditable} />
                </Stack>
                <Field label={translate('invoice.edit.form.other.type')} required>
                    <BaseCombobox<InvoiceType>
                        required
                        readOnly={!props.isEditable}
                        entities={Object.values(InvoiceType)}
                        getEntityLabel={invoiceType => translate(`invoice.type.${invoiceType.toLowerCase().replaceAll(/[-_]/g, '')}`)}
                        getEntityValue={invoiceType => invoiceType.toString()}
                        selectedOptions={[values.type].filter(Boolean)}
                        onOptionSelect={(_, data) => setFieldValue('type', data.optionValue)}
                    />
                </Field>
            </Fieldset>
            {modules.has(ModuleType.Attributes) && (activeAttributeDefinitions.length > 0 || values.attributes?.length > 0) && (
                <Fieldset legend={translate('invoice.edit.form.attributes.label')} canToggle>
                    <FormContainer>
                        <AttributesList
                            {...props}
                            entityType={EntityAttributeType.Invoice}
                            context={[AttributeContextType.Invoice]}
                            viewType={props.isEditable ? AttributeViewType.Input : AttributeViewType.View}
                            orientation={'horizontal'}
                            entity={values}
                            entityId={invoice?.id}
                            setFieldValue={setFieldValue}
                        />
                    </FormContainer>
                </Fieldset>
            )}
            <Fieldset legend={translate('invoice.edit.form.banktransactions.label')} canToggle>
                <BankTransactions {...props} bankTransactions={props.invoice?.bankTransactions} />
            </Fieldset>
        </Stack>
    )
}
